import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { progressAPI, planAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BibleReader from '../components/BibleReader';
import SEO from '../components/SEO';
import { BookOpen, CheckCircle, ChevronRight, AlertCircle, Calendar, Target } from 'lucide-react';
import './TodayReading.css';

const TodayReading = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [todayPlan, setTodayPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [motivation, setMotivation] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [missedDays, setMissedDays] = useState([]);
  const [showMissedDaysWarning, setShowMissedDaysWarning] = useState(false);

  const checkMissedDays = useCallback(async (statusData) => {
    try {
      const progressResponse = await progressAPI.getAllProgress();
      const allProgress = progressResponse.data.progress;
      
      const missed = [];
      for (let day = 1; day < statusData.currentDay; day++) {
        if (!allProgress[day] || !allProgress[day].completedAt) {
          missed.push(day);
        }
      }
      
      setMissedDays(missed);
      setShowMissedDaysWarning(missed.length > 0);
    } catch (err) {
      console.error('Error checking missed days:', err);
    }
  }, []);

  const fetchTodayData = async () => {
    try {
      const statusResponse = await progressAPI.getStatus();
      setStatus(statusResponse.data);

      if (statusResponse.data.started) {
        const planResponse = await planAPI.getDay(statusResponse.data.currentDay);
        setTodayPlan(planResponse.data);
      }
    } catch (err) {
      console.error('Error fetching today data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayData();
    if (user?.languagePreference === 'telugu') {
      setSelectedLanguage('telugu');
    }
  }, []);

  useEffect(() => {
    if (status && status.started) {
      checkMissedDays(status);
    }
  }, [status, checkMissedDays]);

  const handleComplete = async () => {
    if (!status?.currentDay) return;

    setCompleting(true);
    try {
      const response = await progressAPI.completeDay(status.currentDay);
      setMotivation(response.data.motivation);
      setShowSuccess(true);
      
      setTimeout(() => {
        fetchTodayData();
      }, 1000);
    } catch (err) {
      console.error('Error completing day:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleNextDay = () => {
    setShowSuccess(false);
    setMotivation(null);
    navigate('/plan');
  };

  const handleReadMissedDays = () => {
    setShowMissedDaysWarning(false);
    navigate('/plan');
  };

  const handleContinueToday = () => {
    setShowMissedDaysWarning(false);
  };

  const getInspirationMessage = () => {
    const messages = [
      {
        english: "Every step in your Bible journey brings you closer to God. Don't worry about missed days - just start fresh today!",
        telugu: "మీ బైబిల్ ప్రయాణంలో ప్రతి అడుగు మీను దేవునికి దగ్గరగా తీసుకువెళ్తుంది. జాడిన రోజుల గురించి చింతించకండి - కేవలం ఈరోజు కొత్తగా ప్రారంభించండి!"
      },
      {
        english: "God's grace is new every morning. Today is a perfect day to continue your reading journey!",
        telugu: "దేవుని కృప ప్రతి ఉదయం కొత్తది. ఈరోజు మీ చదువు ప్రయాణాన్ని కొనసాగించడానికి సరైన రోజు!"
      },
      {
        english: "Your consistency matters more than perfection. Keep going - you're doing great!",
        telugu: "మీ స్థిరత్వం పరిపూర్ణత కంటే ఎక్కువ ముఖ్యమైనది. కొనసాగండి - మీరు బాగా చేస్తున్నారు!"
      }
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-spinner">Loading today's reading...</div>
        </div>
      </>
    );
  }

  // Show missed days warning
  if (showMissedDaysWarning && missedDays.length > 0) {
    const inspiration = getInspirationMessage();
    return (
      <>
        <Navbar />
        <div className="today-container">
          <div className="container">
            <div className="missed-days-warning card">
              <div className="warning-header">
                <AlertCircle size={48} className="warning-icon" />
                <h2>⚠️ Missed Days Detected</h2>
              </div>
              
              <div className="missed-days-info">
                <p>
                  <strong>You have missed {missedDays.length} day{missedDays.length > 1 ? 's' : ''}:</strong>
                </p>
                <div className="missed-days-list">
                  {missedDays.map(day => (
                    <span key={day} className="missed-day-badge">Day {day}</span>
                  ))}
                </div>
              </div>

              <div className="inspiration-box">
                <Target size={24} className="inspiration-icon" />
                <h3>💪 Today's Inspiration</h3>
                <p className="inspiration-message">
                  {selectedLanguage === 'telugu' ? inspiration.telugu : inspiration.english}
                </p>
              </div>

              <div className="warning-actions">
                <div className="action-option">
                  <h4>
                    <Calendar size={20} />
                    Catch Up First
                  </h4>
                  <p>Complete your missed readings to stay on track</p>
                  <button onClick={handleReadMissedDays} className="btn btn-warning">
                    <BookOpen size={20} />
                    Read Missed Days
                  </button>
                </div>

                <div className="action-option">
                  <h4>
                    <Target size={20} />
                    Continue Today
                  </h4>
                  <p>Read today's passage and catch up later</p>
                  <button onClick={handleContinueToday} className="btn btn-primary">
                    <ChevronRight size={20} />
                    Read Today's Reading
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!status?.started) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="card">
            <h2>Journey Not Started</h2>
            <p>Please start your 365-day Bible reading journey from the dashboard.</p>
            <button onClick={() => navigate('/dashboard')} className="btn btn-primary">
              Go to Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  if (showSuccess && motivation) {
    return (
      <>
        <Navbar />
        <div className="today-container">
          <div className="container">
            <div className="success-card card">
              <CheckCircle size={64} className="success-icon" />
              <h2>Well Done! 🎉</h2>
              <p className="success-message">You completed Day {status.currentDay} of your reading!</p>
              
              <div className="motivation-box">
                <h3>Encouragement</h3>
                <p className="motivation-message">{motivation.message_english}</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="motivation-message telugu">{motivation.message_telugu}</p>
                )}
                {motivation.verse_reference && (
                  <p className="verse-reference">— {motivation.verse_reference}</p>
                )}
              </div>

              <div className="action-buttons">
                <button onClick={handleNextDay} className="btn btn-primary">
                  <ChevronRight size={20} />
                  Go to Next Day
                </button>
                <button onClick={() => navigate('/dashboard')} className="btn btn-outline">
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const todayReadingStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Today's Bible Reading - HolyBible365",
  "description": "Read today's Bible passage from our 365-day reading plan. Available in English and Telugu languages with progress tracking.",
  "url": "https://holybible365.com/today",
  "isPartOf": {
    "@type": "WebSite",
    "name": "HolyBible365",
    "url": "https://holybible365.com"
  },
  "mainEntity": {
    "@type": "CreativeWork",
    "name": "Daily Bible Reading",
    "educationalLevel": "Beginner to Advanced",
    "inLanguage": ["en", "te"]
  }
};

return (
    <>
      <SEO 
        title="Today's Bible Reading - Day {status?.currentDay || 1}"
        description="Read today's Bible passage from our structured 365-day reading plan. Track your progress and grow spiritually with daily Scripture readings in English and Telugu."
        keywords="daily Bible reading, today's Scripture, Bible study plan, spiritual growth, Christian devotion, English Telugu Bible"
        canonicalUrl="https://holybible365.com/today"
        structuredData={todayReadingStructuredData}
      />
      <Navbar />
      <div className="today-container">
        <div className="container">
          <div className="today-header card">
            <div className="header-top">
              <div className="day-badge">Day {status.currentDay} of 365</div>
              <div className="language-selector">
                <button 
                  className={`lang-btn ${selectedLanguage === 'english' ? 'active' : ''}`}
                  onClick={() => setSelectedLanguage('english')}
                >
                  English
                </button>
                <button 
                  className={`lang-btn ${selectedLanguage === 'telugu' ? 'active' : ''}`}
                  onClick={() => setSelectedLanguage('telugu')}
                >
                  తెలుగు
                </button>
              </div>
            </div>
            <h1>Today's Bible Reading</h1>
            {status.todayCompleted && (
              <div className="completed-badge">
                <CheckCircle size={20} />
                Completed
              </div>
            )}
          </div>

          {todayPlan && (
            <>
              <div className="reading-section card">
                <BibleReader 
                  readingText={todayPlan.oldTestament} 
                  title="📖 Old Testament"
                  selectedLanguage={selectedLanguage}
                />
              </div>

              <div className="reading-section card">
                <BibleReader 
                  readingText={todayPlan.newTestament} 
                  title="✝️ New Testament"
                  selectedLanguage={selectedLanguage}
                />
              </div>

              <div className="reading-section card">
                <BibleReader 
                  readingText={todayPlan.psalmsProverbs} 
                  title="🎵 Psalms & Proverbs"
                  selectedLanguage={selectedLanguage}
                />
              </div>

              <div className="action-card card">
                {!status.todayCompleted ? (
                  <>
                    <p className="action-message">
                      Have you completed today's reading? Mark it as done to track your progress!
                    </p>
                    <button 
                      onClick={handleComplete} 
                      className="btn btn-success btn-large"
                      disabled={completing}
                    >
                      <CheckCircle size={20} />
                      {completing ? 'Marking Complete...' : 'Mark as Completed'}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="action-message">
                      ✅ You've already completed today's reading. Great job!
                    </p>
                    <button onClick={() => navigate('/plan')} className="btn btn-primary">
                      View Full Reading Plan
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TodayReading;
